import { useField } from 'formik';
import { memo } from 'react';
import { useLatest } from 'react-use';

import {
    Autocomplete,
    AutocompleteRenderInputParams,
    createFilterOptions,
    TextField,
    UseAutocompleteProps,
} from '@mui/material';
import { InterviewLanguage, PresetLanguages } from '@synergetx/puppet-interview-types';

const LANGUAGE_OPTIONS = Object.values(PresetLanguages);
const filter = createFilterOptions<InterviewLanguage>();

const LanguageSelectAutocomplete = () => {
    const [{ value: languages }, { error, touched }, { setValue: setLanguages }] =
        useField<InterviewLanguage[]>('languages');

    // Show error if field has been touched or if there are no languages (validation runs on mount)
    const showError = (touched || languages.length === 0) && (!!error || languages.length === 0);
    const errorMessage =
        error || (languages.length === 0 ? 'At least one language is required' : '');

    const handleChange: UseAutocompleteProps<InterviewLanguage, true, false, false>['onChange'] =
        useLatest((_event: React.SyntheticEvent<Element, Event>, newValue: InterviewLanguage[]) =>
            setLanguages(
                newValue.map((language) => language.replace('Add "', '').replace('"', '')),
            ),
        ).current;

    return (
        <Autocomplete
            multiple
            id="Language-Select-Autocomplete"
            options={LANGUAGE_OPTIONS}
            getOptionLabel={(option) => {
                if (option === PresetLanguages.HAITIAN_CREOLE) {
                    return 'Haitian Creole';
                } else {
                    return option;
                }
            }}
            value={languages}
            onChange={handleChange}
            filterOptions={(options, params) => {
                const filtered = filter(options, params);

                const { inputValue } = params;
                // Suggest the creation of a new value
                const isExisting = options.some((option) => inputValue === option);
                if (inputValue !== '' && !isExisting) {
                    filtered.push(`Add "${inputValue}"`);
                }

                return filtered;
            }}
            filterSelectedOptions
            sx={{ mb: 1, mt: 2, width: '100%' }}
            renderInput={(params: AutocompleteRenderInputParams) => (
                <TextField
                    {...params}
                    label="Available Languages"
                    placeholder="Select language..."
                    error={showError}
                    helperText={showError ? errorMessage : ''}
                />
            )}
        />
    );
};

export default memo(LanguageSelectAutocomplete);
